/**
 * Tabs Component
 * An accessible tabs web component following WAI-ARIA tabs pattern.
 *
 * Features:
 * - Keyboard navigation (Arrow keys, Home, End)
 * - ARIA attributes for screen readers
 * - Automatic activation on focus (optional)
 * - First tab active by default
 * - Dynamic tab button generation from panel data attributes
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
 */
class TabsComponent extends HTMLElement {
  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MediaQueryList | null} */
  #desktopMediaQuery = null;

  /** @type {number | null} */
  #equalHeightFrame = null;

  /**
   * Get all tab buttons
   * @returns {HTMLButtonElement[]}
   */
  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /**
   * Get all tab panels
   * @returns {HTMLElement[]}
   */
  get panels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]'));
  }

  /**
   * Get the currently active tab
   * @returns {HTMLButtonElement | null}
   */
  get activeTab() {
    return this.querySelector('[role="tab"][aria-selected="true"]');
  }

  /**
   * Get the layout direction
   * @returns {'horizontal' | 'vertical'}
   */
  get layout() {
    return this.dataset.layout || "horizontal";
  }

  /**
   * Get the tablist container
   * @returns {HTMLElement | null}
   */
  get tablist() {
    return this.querySelector('[role="tablist"]');
  }

  /**
   * Initialize the component when connected to the DOM
   */
  connectedCallback() {
    // If the element is disconnected and reconnected (theme editor updates, section rerenders),
    // the AbortController used for event listeners will have been aborted. A new controller is
    // required or addEventListener({ signal }) will no-op.
    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    // Use requestAnimationFrame to ensure DOM is fully rendered
    requestAnimationFrame(() => {
      this.#moveNonPanelBlocksAfterPanels();
      this.#buildTabButtons();
      this.#initializeTabs();
      this.#setupEqualPanelHeights();
      this.#scheduleEqualPanelHeights();
    });
  }

  /**
   * Keep extra section blocks from contributing to the tab panel height.
   */
  #moveNonPanelBlocksAfterPanels() {
    const panelsWrapper = this.querySelector(".tabs-section__panels");

    if (!(panelsWrapper instanceof HTMLElement)) return;

    const extraBlocks = Array.from(panelsWrapper.children).filter((child) => {
      if (child.getAttribute("role") === "tabpanel") return false;
      return !child.querySelector(":scope > [role='tabpanel']");
    });
    let insertAfter = panelsWrapper;

    extraBlocks.forEach((block) => {
      block.classList.add("tabs-section__after-panels");
      insertAfter.after(block);
      insertAfter = block;
    });
  }

  /**
   * Build tab buttons dynamically from panel data attributes
   * This is required because external theme blocks' settings cannot be
   * accessed from section.blocks in the parent section.
   */
  #buildTabButtons() {
    const tablist = this.tablist;
    const panels = this.panels;

    if (!tablist || panels.length === 0) {
      return;
    }

    // Clear any existing buttons
    tablist.innerHTML = "";

    const sectionId = tablist.dataset.sectionId;

    panels.forEach((panel, index) => {
      const blockId = panel.dataset.blockId;
      const label = panel.dataset.tabLabel || "Tab";

      // Create button element
      const button = document.createElement("button");
      button.type = "button";
      button.role = "tab";
      button.className = "tabs-section__tab";
      button.id = `tab-${sectionId}-${blockId}`;
      button.setAttribute("aria-controls", panel.id);
      button.setAttribute("aria-selected", index === 0 ? "true" : "false");
      button.setAttribute("tabindex", index === 0 ? "0" : "-1");
      button.dataset.tabIndex = index.toString();

      // Prefer cloning the Liquid-rendered template from the panel so icons/images render
      // using the theme's canonical markup (snippets/icon-or-image.liquid).
      const template = panel.querySelector(
        "template.tabs-section__tab-template"
      );
      if (template instanceof HTMLTemplateElement) {
        button.append(template.content.cloneNode(true));
      } else {
        // Fallback: label only (and keep icon as a no-op, since JS doesn't know the theme icon markup)
        const labelSpan = document.createElement("span");
        labelSpan.className = "tabs-section__tab-label";
        labelSpan.textContent = label;
        button.appendChild(labelSpan);
      }

      tablist.appendChild(button);

      // Update panel's aria-labelledby to match the new button id
      panel.setAttribute("aria-labelledby", button.id);
    });

    // Set up event listeners on each tab
    const { signal } = this.#controller;
    this.tabs.forEach((tab) => {
      tab.addEventListener("click", this.#handleTabClick, { signal });
      tab.addEventListener("keydown", this.#handleKeyDown, { signal });
    });
  }

  /**
   * Initialize tabs - show first panel, hide others
   */
  #initializeTabs() {
    const tabs = this.tabs;
    const panels = this.panels;

    if (tabs.length === 0 || panels.length === 0) {
      return;
    }

    // Find the first selected tab or default to first tab
    let activeIndex = 0;
    tabs.forEach((tab, index) => {
      if (tab.getAttribute("aria-selected") === "true") {
        activeIndex = index;
      }
    });

    // Set tab states
    tabs.forEach((tab, index) => {
      if (index === activeIndex) {
        tab.setAttribute("aria-selected", "true");
        tab.setAttribute("tabindex", "0");
      } else {
        tab.setAttribute("aria-selected", "false");
        tab.setAttribute("tabindex", "-1");
      }
    });

    // Set panel states
    panels.forEach((panel, index) => {
      if (index === activeIndex) {
        panel.hidden = false;
        panel.setAttribute("data-active", "true");
      } else {
        panel.hidden = true;
        panel.removeAttribute("data-active");
      }
    });

    this.#scheduleEqualPanelHeights();
  }

  /**
   * Clean up when component is removed from DOM
   */
  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#equalHeightFrame !== null) {
      cancelAnimationFrame(this.#equalHeightFrame);
      this.#equalHeightFrame = null;
    }
  }

  /**
   * Handle tab button click
   * @param {MouseEvent} event
   */
  #handleTabClick = (event) => {
    const tab = event.currentTarget;
    if (tab instanceof HTMLButtonElement) {
      this.#activateTab(tab, { scrollToSectionStart: true });
    }
  };

  /**
   * Handle keyboard navigation
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    const { key } = event;
    const tabs = this.tabs;
    const currentIndex = tabs.indexOf(event.currentTarget);

    // Determine navigation keys based on layout
    const isVertical = this.layout === "vertical";
    const prevKey = isVertical ? "ArrowUp" : "ArrowLeft";
    const nextKey = isVertical ? "ArrowDown" : "ArrowRight";

    let newIndex = currentIndex;

    switch (key) {
      case prevKey:
        event.preventDefault();
        newIndex = currentIndex - 1;
        if (newIndex < 0) newIndex = tabs.length - 1;
        break;

      case nextKey:
        event.preventDefault();
        newIndex = currentIndex + 1;
        if (newIndex >= tabs.length) newIndex = 0;
        break;

      case "Home":
        event.preventDefault();
        newIndex = 0;
        break;

      case "End":
        event.preventDefault();
        newIndex = tabs.length - 1;
        break;

      default:
        return;
    }

    // Move focus and activate the new tab
    const newTab = tabs[newIndex];
    if (newTab) {
      newTab.focus();
      this.#activateTab(newTab);
    }
  };

  /**
   * Activate a specific tab and show its panel
   * @param {HTMLButtonElement} tab - The tab to activate
   * @param {{ scrollToSectionStart?: boolean }} [options]
   */
  #activateTab(tab, { scrollToSectionStart = false } = {}) {
    const panelId = tab.getAttribute("aria-controls");
    const panel = panelId ? this.querySelector(`#${panelId}`) : null;

    if (!panel) return;

    // Deactivate all tabs
    this.tabs.forEach((t) => {
      t.setAttribute("aria-selected", "false");
      t.setAttribute("tabindex", "-1");
    });

    // Hide all panels
    this.panels.forEach((p) => {
      p.hidden = true;
      p.removeAttribute("data-active");
    });

    // Activate the selected tab
    tab.setAttribute("aria-selected", "true");
    tab.setAttribute("tabindex", "0");

    // Show the corresponding panel
    panel.hidden = false;
    panel.setAttribute("data-active", "true");
    this.#scheduleEqualPanelHeights();

    if (scrollToSectionStart && this.#shouldScrollToSectionStart()) {
      this.#scrollToSectionStart();
    }

    // Dispatch custom event for external listeners
    this.dispatchEvent(
      new CustomEvent("tab-change", {
        detail: {
          tab,
          panel,
          index: this.tabs.indexOf(tab),
        },
        bubbles: true,
      })
    );
  }

  /**
   * Only auto-scroll on the mobile sticky variant.
   * @returns {boolean}
   */
  #shouldScrollToSectionStart() {
    return (
      this.classList.contains("tabs-section--mobile-sticky-tabs") &&
      window.matchMedia("(max-width: 989px)").matches
    );
  }

  /**
   * Keep desktop tab panels from changing the section height as users switch tabs.
   */
  #setupEqualPanelHeights() {
    if (!this.#desktopMediaQuery) {
      this.#desktopMediaQuery = window.matchMedia("(min-width: 990px)");
      this.#desktopMediaQuery.addEventListener(
        "change",
        this.#scheduleEqualPanelHeights,
        { signal: this.#controller.signal }
      );
    }

    if (!this.#resizeObserver && "ResizeObserver" in window) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleEqualPanelHeights);
      this.#resizeObserver.observe(this);
    }

    this.querySelectorAll("img").forEach((image) => {
      if (image.complete) return;
      image.addEventListener("load", this.#scheduleEqualPanelHeights, {
        signal: this.#controller.signal,
      });
    });
  }

  /**
   * Schedule a single equal-height pass.
   */
  #scheduleEqualPanelHeights = () => {
    if (this.#equalHeightFrame !== null) return;

    this.#equalHeightFrame = requestAnimationFrame(() => {
      this.#equalHeightFrame = null;
      this.#syncEqualPanelHeights();
    });
  };

  /**
   * Measure every panel, including currently hidden panels, and reserve the tallest height.
   * CH tabs only reserve the active panel height so responsive card layouts stay natural.
   */
  #syncEqualPanelHeights() {
    const panelsWrapper = this.querySelector(".tabs-section__panels");
    const panels = this.panels;

    if (!(panelsWrapper instanceof HTMLElement) || panels.length <= 1) {
      this.style.removeProperty("--tabs-panels-min-height");
      return;
    }

    if (!this.#desktopMediaQuery?.matches) {
      this.style.removeProperty("--tabs-panels-min-height");
      return;
    }

    const wrapperWidth = panelsWrapper.getBoundingClientRect().width;
    if (wrapperWidth <= 0) return;

    if (this.classList.contains("ch-tabs-section")) {
      const activePanel = panels.find(
        (panel) => panel.getAttribute("data-active") === "true" && !panel.hidden
      );
      const activePanelHeight = activePanel?.getBoundingClientRect().height || 0;

      if (activePanelHeight > 0) {
        this.style.setProperty(
          "--tabs-panels-min-height",
          `${Math.ceil(activePanelHeight)}px`
        );
      } else {
        this.style.removeProperty("--tabs-panels-min-height");
      }

      return;
    }

    let tallestPanelHeight = 0;

    panels.forEach((panel) => {
      const wasHidden = panel.hidden;
      const previousDataActive = panel.getAttribute("data-active");
      const previousStyle = panel.getAttribute("style");

      panel.hidden = false;
      panel.setAttribute("data-active", "true");
      panel.style.position = "absolute";
      panel.style.visibility = "hidden";
      panel.style.pointerEvents = "none";
      panel.style.width = `${wrapperWidth}px`;
      panel.style.display = "block";

      tallestPanelHeight = Math.max(
        tallestPanelHeight,
        panel.getBoundingClientRect().height
      );

      panel.hidden = wasHidden;
      if (previousDataActive === null) {
        panel.removeAttribute("data-active");
      } else {
        panel.setAttribute("data-active", previousDataActive);
      }

      if (previousStyle === null) {
        panel.removeAttribute("style");
      } else {
        panel.setAttribute("style", previousStyle);
      }
    });

    if (tallestPanelHeight > 0) {
      this.style.setProperty(
        "--tabs-panels-min-height",
        `${Math.ceil(tallestPanelHeight)}px`
      );
    }
  }

  /**
   * Scroll the viewport to the start of the tabs section, accounting for the sticky header.
   */
  #scrollToSectionStart() {
    const computedStyles = window.getComputedStyle(this);
    const stickyOffset =
      Number.parseFloat(
        computedStyles.getPropertyValue("--tabs-mobile-sticky-top-offset")
      ) || 0;
    const stickyNudge =
      Number.parseFloat(
        computedStyles.getPropertyValue("--tabs-mobile-sticky-visual-nudge")
      ) || 0;
    const targetTop =
      this.getBoundingClientRect().top +
      window.scrollY -
      stickyOffset +
      stickyNudge;

    window.scrollTo({
      top: Math.max(0, targetTop),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });
  }
}

// Register the custom element
if (!customElements.get("tabs-component")) {
  customElements.define("tabs-component", TabsComponent);
}
